import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  ColorPicker,
  Flex,
  Popover,
  Segmented,
  Tooltip,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import {
  Image as ImageIcon,
  ImagePlus,
  Info,
  Palette,
  Settings,
  Upload as UploadIcon
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  attachmentsRetrieveOptions,
  attachmentsVisibilityPartialUpdate,
  organizationsOrganizationsPartialUpdateMutation,
  organizationsOrganizationsRetrieveQueryKey,
  type Organization
} from 'src/api/main'
import { useHasWorkspacePermissions } from 'src/hooks'
import { usePresignedUpload } from 'src/hooks/usePresignedUpload'
import {
  isSvgFile,
  MAX_SVG_MB,
  storedPreviewUrl,
  SVG_ACCEPT,
  svgRejection,
  uploadFiles,
  useShowApiErrors,
  WorkspacePermissionCode
} from 'src/utils'

const { Text } = Typography

const SIGN_IN_BACKGROUND_KEY = 'signInBackground'
const MAX_BACKGROUND_MB = 5

const DEFAULT_GRADIENT_STOPS = [
  { color: '#134E4A', percent: 0 },
  { color: '#0B1420', percent: 100 }
]

const GRADIENT_PRESETS = [
  'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
  'linear-gradient(135deg, #059669 0%, #34D399 100%)',
  'linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)',
  'linear-gradient(140deg, #052E2B 0%, #0F766E 40%, #CCFBF1 100%)',
  'linear-gradient(125deg, #064E3B 0%, #22C55E 45%, #A7F3D0 100%)',
  'linear-gradient(145deg, #0F172A 0%, #1D4ED8 42%, #38BDF8 100%)',
  'linear-gradient(130deg, #075985 0%, #06B6D4 48%, #C7D2FE 100%)',
  'linear-gradient(150deg, #172554 0%, #2563EB 36%, #67E8F9 100%)',
  'linear-gradient(120deg, #083344 0%, #0284C7 52%, #DBEAFE 100%)',
  'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
  'linear-gradient(140deg, #2E1065 0%, #7C3AED 44%, #F0ABFC 100%)',
  'linear-gradient(125deg, #312E81 0%, #8B5CF6 52%, #22D3EE 100%)',
  'linear-gradient(145deg, #1E1B4B 0%, #6D28D9 45%, #F9A8D4 100%)',
  'linear-gradient(135deg, #581C87 0%, #C026D3 46%, #FDE68A 100%)',
  'linear-gradient(135deg, #D97706 0%, #FBBF24 100%)',
  'linear-gradient(135deg, #7C2D12 0%, #EA580C 44%, #FACC15 100%)',
  'linear-gradient(145deg, #831843 0%, #F97316 48%, #FDE68A 100%)',
  'linear-gradient(125deg, #451A03 0%, #CA8A04 42%, #FEF3C7 100%)',
  'linear-gradient(140deg, #9A3412 0%, #EF4444 45%, #FDBA74 100%)',
  'linear-gradient(135deg, #DB2777 0%, #F472B6 100%)',
  'linear-gradient(140deg, #881337 0%, #E11D48 45%, #FDBA74 100%)',
  'linear-gradient(135deg, #DC2626 0%, #F87171 100%)',
  'linear-gradient(130deg, #4C0519 0%, #BE123C 46%, #FBCFE8 100%)',
  'linear-gradient(135deg, #1E293B 0%, #0B1420 100%)',
  'linear-gradient(150deg, #020617 0%, #164E63 45%, #14B8A6 100%)',
  'linear-gradient(135deg, #111827 0%, #4338CA 46%, #0F172A 100%)',
  'linear-gradient(145deg, #020617 0%, #334155 44%, #94A3B8 100%)'
]

type BackgroundMode = 'image' | 'gradient'
type SignInBackgroundImage = { id: number; file: string; is_public: true }
type SignInBackgroundValue = string | number | SignInBackgroundImage | null

type OrganizationWithSettings = Organization & {
  settings?: Record<string, unknown> | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getSignInBackground = (
  settings: OrganizationWithSettings['settings']
): SignInBackgroundValue => {
  if (!isRecord(settings)) {
    return null
  }

  const value = settings[SIGN_IN_BACKGROUND_KEY]

  if (typeof value === 'string') {
    const trimmed = value.trim()

    return trimmed ? trimmed : null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  /**
   * Saved as `{ id, file, is_public }`; a bare id from before that is read back
   * expanded by the backend — `{ id, title, file, … }`. A public attachment
   * keeps a stable URL, so it is taken as-is and needs no lookup. Anything else
   * falls back to the id: a private attachment's `file` is a pre-signed URL
   * that expires, so it has to be fetched fresh.
   */
  if (isRecord(value) && typeof value.id === 'number') {
    if (!Number.isFinite(value.id)) {
      return null
    }

    return value.is_public === true && typeof value.file === 'string'
      ? { id: value.id, file: value.file, is_public: true }
      : value.id
  }

  return null
}

const buildSettingsPatch = (
  settings: OrganizationWithSettings['settings'],
  signInBackground: SignInBackgroundValue
): Record<string, unknown> => ({
  ...(isRecord(settings) ? settings : {}),
  [SIGN_IN_BACKGROUND_KEY]: signInBackground
})

const isImageBackground = (value: string) =>
  /^(https?:\/\/|\/|data:|blob:)/i.test(value)

const getAttachmentBackgroundId = (
  value: SignInBackgroundValue
): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0 ? value : null
  }

  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const id = Number(value)

    return Number.isSafeInteger(id) && id > 0 ? id : null
  }

  return null
}

const GRADIENT_STOP_RE = /(rgba?\([^)]*\)|#[0-9a-f]+)\s+([\d.]+)%/gi

const parseGradientStops = (
  value: string
): { color: string; percent: number }[] | null => {
  const stops = Array.from(value.matchAll(GRADIENT_STOP_RE)).map((match) => ({
    color: match[1],
    percent: Number(match[2])
  }))

  return stops.length >= 2 ? stops : null
}

const backgroundPreviewStyle = (value: string | null): CSSProperties =>
  value
    ? isImageBackground(value)
      ? {
          backgroundImage: `url(${value})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }
      : { background: value }
    : { background: 'var(--ant-color-fill-tertiary)' }

type Props = {
  organization: Organization
}

export const SignInBackgroundField = ({ organization }: Props) => {
  const { t } = useTranslation()
  // The sign-in background is a workspace attachment.
  const [mayUploadAttachments, mayDeleteAttachments] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.ATTACHMENTS_UPLOAD,
      WorkspacePermissionCode.ATTACHMENTS_DELETE
    ])
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const {
    uploadFile,
    submitWebhook,
    isLoading: isUploadingBackground
  } = usePresignedUpload()

  const organizationWithSettings = organization as OrganizationWithSettings
  const savedBackground = getSignInBackground(organizationWithSettings.settings)
  const savedBackgroundAttachmentId = getAttachmentBackgroundId(savedBackground)
  const { data: savedBackgroundAttachment } = useQuery({
    ...attachmentsRetrieveOptions({
      path: { id: savedBackgroundAttachmentId ?? 0 }
    }),
    enabled: savedBackgroundAttachmentId !== null
  })

  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerMode, setPickerMode] = useState<BackgroundMode>('image')
  const [gradientDraft, setGradientDraft] = useState<string | null>(null)
  const [imageDraft, setImageDraft] = useState<string | null>(null)
  const imageDraftRef = useRef<string | null>(null)

  const releaseImageDraft = useCallback(() => {
    if (imageDraftRef.current) {
      URL.revokeObjectURL(imageDraftRef.current)
      imageDraftRef.current = null
    }
  }, [])

  const clearImageDraft = useCallback(() => {
    releaseImageDraft()
    setImageDraft(null)
  }, [releaseImageDraft])

  useEffect(() => releaseImageDraft, [releaseImageDraft])

  const { mutateAsync: patchOrganization, isPending: isSavingBackground } =
    useMutation({
      ...organizationsOrganizationsPartialUpdateMutation(),
      onSuccess: async () => {
        message.success(t('workspace.settings.saved'))
        await queryClient.invalidateQueries({
          queryKey: organizationsOrganizationsRetrieveQueryKey({
            path: { id: organization.id }
          })
        })
      },
      onError: (error) => showApiErrors(error)
    })

  const patchSignInBackground = useCallback(
    async (value: SignInBackgroundValue) => {
      await patchOrganization({
        path: { id: organization.id },
        body: {
          settings: buildSettingsPatch(organizationWithSettings.settings, value)
        } as never
      })
    },
    [organization.id, organizationWithSettings.settings, patchOrganization]
  )

  const beforeBackgroundUpload = useCallback(
    async (file: RcFile) => {
      const isImage = isSvgFile(file) || file.type.startsWith('image/')
      if (!isImage) {
        message.error(t('only_upload_image'))
      }

      const fitsLimit = file.size / 1024 / 1024 <= MAX_BACKGROUND_MB
      if (isImage && !fitsLimit) {
        message.error(t('workspace.settings.branding.fileTooLarge'))
      }

      if (!isImage || !fitsLimit) {
        return Upload.LIST_IGNORE
      }

      const svgProblem = await svgRejection(file)
      if (svgProblem) {
        message.error(t(`svgUpload.${svgProblem}`, { size: MAX_SVG_MB }))

        return Upload.LIST_IGNORE
      }

      return true
    },
    [message, t]
  )

  const handleBackgroundUpload: UploadProps['customRequest'] = useCallback(
    async ({ file, onError, onSuccess }) => {
      const rcFile = file as RcFile

      releaseImageDraft()
      // The draft is the copy that will be stored, so an SVG shows as sanitized.
      const localUrl = await storedPreviewUrl(rcFile)
      imageDraftRef.current = localUrl
      setImageDraft(localUrl)

      try {
        const result = await uploadFile(rcFile, Number(organization.id))

        await uploadFiles(
          [
            {
              uid: rcFile.uid,
              name: rcFile.name,
              status: 'done',
              originFileObj: rcFile,
              response: {
                upload_url: result.upload_url,
                webhook_data: result.webhook_data
              }
            } as UploadFile
          ],
          { submitWebhook, onError: (error) => showApiErrors(error) }
        )

        const { data: attachment } = await attachmentsVisibilityPartialUpdate({
          path: { id: result.id },
          body: { is_public: true },
          throwOnError: true
        })

        await patchSignInBackground({
          id: result.id,
          file: attachment.file,
          is_public: true
        })
        clearImageDraft()
        setPickerOpen(false)
        onSuccess?.({ ...result, file: attachment.file }, file)
      } catch (error) {
        clearImageDraft()
        onError?.(error as Error)
      }
    },
    [
      clearImageDraft,
      organization.id,
      patchSignInBackground,
      releaseImageDraft,
      showApiErrors,
      submitWebhook,
      uploadFile
    ]
  )

  const handleApplyGradient = useCallback(async () => {
    if (!gradientDraft) {
      return
    }

    await patchSignInBackground(gradientDraft)
    setGradientDraft(null)
    setPickerOpen(false)
  }, [gradientDraft, patchSignInBackground])

  const handleBackgroundRemove = useCallback(async () => {
    await patchSignInBackground(null)
    setGradientDraft(null)
    clearImageDraft()
    setPickerOpen(false)
  }, [clearImageDraft, patchSignInBackground])

  const handlePickerOpenChange = useCallback(
    (open: boolean) => {
      setPickerOpen(open)
      if (open) {
        setPickerMode(
          typeof savedBackground === 'string' &&
            !savedBackgroundAttachmentId &&
            !isImageBackground(savedBackground)
            ? 'gradient'
            : 'image'
        )
      } else {
        setGradientDraft(null)
        clearImageDraft()
      }
    },
    [clearImageDraft, savedBackground, savedBackgroundAttachmentId]
  )

  const isBackgroundBusy = isUploadingBackground || isSavingBackground

  const savedBackgroundImage =
    typeof savedBackground === 'object' && savedBackground !== null
      ? savedBackground
      : null
  const savedBackgroundUrl = savedBackgroundImage
    ? savedBackgroundImage.file
    : typeof savedBackground === 'string' && isImageBackground(savedBackground)
      ? savedBackground
      : (savedBackgroundAttachment?.file ?? null)
  const savedGradient =
    typeof savedBackground === 'string' &&
    !savedBackgroundAttachmentId &&
    !isImageBackground(savedBackground)
      ? savedBackground
      : null
  const activeGradient = gradientDraft ?? savedGradient
  const pickerValue = activeGradient
    ? (parseGradientStops(activeGradient) ?? activeGradient)
    : DEFAULT_GRADIENT_STOPS

  const draftBackground = pickerMode === 'gradient' ? gradientDraft : imageDraft
  const previewBackground =
    draftBackground ?? savedGradient ?? savedBackgroundUrl

  const pickerContent = (
    <Flex vertical gap={10} style={{ width: 310 }}>
      <Flex align="center" gap={5}>
        <Text style={{ fontSize: 13, fontWeight: 600 }}>
          {t('workspace.settings.branding.signInBackground')}
        </Text>
        <Tooltip title={t('workspace.settings.branding.pickerHint')}>
          <Info
            size={13}
            color="var(--ant-color-text-tertiary)"
            style={{ cursor: 'help' }}
          />
        </Tooltip>
      </Flex>
      <Segmented
        block
        size="small"
        value={pickerMode}
        onChange={(value) => setPickerMode(value as BackgroundMode)}
        options={[
          {
            value: 'image',
            // Icon and text sit in the label as one centred row: Segmented's
            // own `icon` slot is baseline-aligned, which no amount of
            // `verticalAlign` nudging lines up with the text.
            label: (
              <Flex align="center" justify="center" gap={6}>
                <ImageIcon size={13} />
                {t('workspace.settings.branding.bgImage')}
              </Flex>
            )
          },
          {
            value: 'gradient',
            label: (
              <Flex align="center" justify="center" gap={6}>
                <Palette size={13} />
                {t('workspace.settings.branding.bgGradient')}
              </Flex>
            )
          }
        ]}
      />

      {pickerMode === 'image' ? (
        <Upload.Dragger
          accept={`image/*,${SVG_ACCEPT}`}
          disabled={isBackgroundBusy || !mayUploadAttachments}
          beforeUpload={beforeBackgroundUpload}
          customRequest={handleBackgroundUpload}
          maxCount={1}
          showUploadList={false}
        >
          <Flex vertical align="center" gap={6} style={{ padding: '4px 0' }}>
            <span
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: 'var(--ant-color-primary-bg)',
                color: 'var(--ant-color-primary)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <UploadIcon size={16} />
            </span>
            <Text style={{ fontSize: 12, fontWeight: 500 }}>
              {t('workspace.settings.branding.dragDrop')}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('workspace.settings.branding.fileTypes', {
                svgSize: MAX_SVG_MB
              })}
            </Text>
          </Flex>
        </Upload.Dragger>
      ) : (
        <Flex vertical gap={8}>
          <Flex gap={6} wrap="wrap">
            {GRADIENT_PRESETS.map((preset, index) => (
              <button
                key={preset}
                type="button"
                aria-label={`${t('workspace.settings.branding.bgGradient')} ${index + 1}`}
                onClick={() => setGradientDraft(preset)}
                style={{
                  width: 28,
                  height: 28,
                  padding: 0,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: preset,
                  boxShadow:
                    activeGradient === preset
                      ? '0 0 0 2px var(--ant-color-bg-elevated), 0 0 0 4px var(--ant-color-primary)'
                      : 'none'
                }}
              />
            ))}
          </Flex>
          <ColorPicker
            mode={['single', 'gradient']}
            value={pickerValue}
            size="small"
            showText
            onChange={(color) => setGradientDraft(color.toCssString())}
            getPopupContainer={(node) => node.parentElement ?? document.body}
          />
          <Button
            type="primary"
            size="small"
            block
            disabled={!gradientDraft}
            loading={isSavingBackground}
            onClick={handleApplyGradient}
          >
            {t('workspace.settings.branding.apply')}
          </Button>
        </Flex>
      )}

      {savedBackground && mayDeleteAttachments ? (
        <Button
          danger
          type="text"
          size="small"
          loading={isSavingBackground}
          onClick={handleBackgroundRemove}
        >
          {t('workspace.settings.branding.remove')}
        </Button>
      ) : null}
    </Flex>
  )

  return (
    <>
      <Flex align="center" gap={5} style={{ marginBottom: 2, minHeight: 22 }}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--ant-color-text)'
          }}
        >
          {t('workspace.settings.branding.signInBackground')}
        </Text>
        <Popover
          trigger="click"
          open={pickerOpen}
          onOpenChange={handlePickerOpenChange}
          content={pickerContent}
        >
          <Tooltip title={t('workspace.settings.branding.configure')}>
            <Button
              type="text"
              size="small"
              aria-label={t('workspace.settings.branding.configure')}
              icon={<Settings size={13} />}
              style={{ width: 20, height: 20, padding: 0 }}
            />
          </Tooltip>
        </Popover>
      </Flex>
      <Flex
        aria-hidden
        style={{
          width: 300,
          maxWidth: '100%',
          height: 112,
          borderRadius: 8,
          border: '1px solid var(--ant-color-border-secondary)',
          overflow: 'hidden',
          background: 'var(--ant-color-bg-container)'
        }}
      >
        <Flex
          vertical
          justify="center"
          gap={6}
          style={{ flex: '1 1 55%', minWidth: 0, padding: '0 16px' }}
        >
          <div
            style={{
              width: 34,
              height: 6,
              borderRadius: 3,
              background: 'var(--ant-color-fill)'
            }}
          />
          <div
            style={{
              width: '70%',
              height: 5,
              borderRadius: 3,
              background: 'var(--ant-color-fill-secondary)'
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 3,
              border: '1px solid var(--ant-color-border)'
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 3,
              background: 'var(--ant-color-primary)'
            }}
          />
        </Flex>
        {/* plain div: antd Flex has `:empty { display: none }`, and this panel
            is empty whenever a background is set */}
        <div
          style={{
            flex: '1 1 45%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...backgroundPreviewStyle(previewBackground)
          }}
        >
          {!previewBackground ? (
            <ImagePlus size={16} color="var(--ant-color-text-quaternary)" />
          ) : null}
        </div>
      </Flex>
    </>
  )
}
